// client/src/components/stats/rfiBenchmarks.ts
//
// Rangos saludables de RFI (Raise First In) por posición y stack depth.
// Basado en consenso de solver studies (Snowie, PIO) y coaching de MTT
// modernos. Los rangos son aproximados — el play exacto depende del meta
// del stake — pero capturan la dirección correcta.

import { OpenPosition } from '../../utils/pokerStats/types';
import { StackKey } from './leakRanges';

export interface RfiBenchmark {
  min: number;
  max: number;
}

// Stack key → posición → rango
export const RFI_BENCHMARKS: Record<StackKey, Record<OpenPosition, RfiBenchmark>> = {
  short: {
    // <25BB: rangos más wide en todas las posiciones por push/fold dynamics
    EP:  { min: 8,  max: 13 },
    HJ:  { min: 12, max: 18 },
    CO:  { min: 18, max: 26 },
    BTN: { min: 36, max: 46 },
    SB:  { min: 38, max: 50 },
  },
  mid: {
    // 25-50BB: rangos clásicos de "deepish" play
    EP:  { min: 10, max: 15 },
    HJ:  { min: 14, max: 19 },
    CO:  { min: 22, max: 28 },
    BTN: { min: 38, max: 48 },
    SB:  { min: 30, max: 42 },
  },
  deep: {
    // >50BB: rangos clásicos GTO
    EP:  { min: 11, max: 16 },
    HJ:  { min: 15, max: 20 },
    CO:  { min: 22, max: 28 },
    BTN: { min: 40, max: 50 },
    SB:  { min: 32, max: 42 },
  },
  all: {
    // Promedio ponderado — útil cuando el user no filtra por stack
    EP:  { min: 10, max: 15 },
    HJ:  { min: 14, max: 19 },
    CO:  { min: 20, max: 27 },
    BTN: { min: 38, max: 47 },
    SB:  { min: 32, max: 44 },
  },
};

export type RfiStatus = 'ok' | 'warn' | 'critical';

// Evalúa un valor de RFI contra el rango saludable.
// Reusa la misma lógica que evalStat para consistency.
export function evalRfi(position: OpenPosition, value: number, stack: StackKey): RfiStatus {
  const stackBenchmarks = RFI_BENCHMARKS[stack] || RFI_BENCHMARKS.all;
  const r = stackBenchmarks[position];
  if (!r) return 'ok';
  if (value >= r.min && value <= r.max) return 'ok';
  const gap = value > r.max ? value - r.max : r.min - value;
  const range = r.max - r.min || 5;
  return gap > range * 1.5 ? 'critical' : 'warn';
}

export function getRfiBenchmark(position: OpenPosition, stack: StackKey): RfiBenchmark | null {
  const stackBenchmarks = RFI_BENCHMARKS[stack] || RFI_BENCHMARKS.all;
  return stackBenchmarks[position] || null;
}
